import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import SiteProofLogo from '../../components/SiteProofLogo';

// Role → dashboard path mapping
const ROLE_DASHBOARD = {
  admin: '/admin-dashboard',
  site_manager: '/site-manager-dashboard',
  worker: '/worker-home',
  client: '/client-dashboard',
};

// ── Error Boundary ────────────────────────────────────────────────────────────
class InviteSetupErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state?.hasError) {
      return (
        <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 max-w-md w-full text-center">
            <div className="w-12 h-12 bg-red-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Something went wrong</h2>
            <p className="text-gray-400 text-sm mb-6">An unexpected error occurred. Please try again or contact support.</p>
            <button
              onClick={() => window.location.href = '/login'}
              className="w-full py-3 px-6 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors text-sm"
            >
              Go to Sign In
            </button>
          </div>
        </div>
      );
    }
    return this.props?.children;
  }
}

// ── Main Component ────────────────────────────────────────────────────────────
const InviteSetupInner = () => {
  const navigate = useNavigate();

  // stage: 'loading' | 'ready' | 'verifying' | 'form' | 'submitting' | 'error'
  //   loading    → initial mount, extracting URL params
  //   ready      → token extracted, awaiting user click (defeats Safe Links prefetch)
  //   verifying  → user clicked, verifyOtp in progress
  //   form       → verified, user fills in name/password
  //   submitting → form submitted, finalizing profile
  //   error      → fatal error, token missing/invalid/consumed
  const [stage, setStage] = useState('loading');
  const [fatalError, setFatalError] = useState('');

  // Token params captured from URL on mount, used only after user click
  const [pendingToken, setPendingToken] = useState(null);

  // Resolved invite data
  const [inviteEmail, setInviteEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [inviteRole, setInviteRole] = useState('worker');
  const [inviteCompanyId, setInviteCompanyId] = useState(null);
  const [inviteSiteId, setInviteSiteId] = useState(null);
  const [resolvedUserId, setResolvedUserId] = useState(null);

  // Form fields
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');

  // Fresh-invite form state (shown on the error stage)
  const [freshInviteEmail, setFreshInviteEmail] = useState('');
  const [freshInviteState, setFreshInviteState] = useState('idle'); // idle | sending | sent | rate_limited | error
  const [freshInviteError, setFreshInviteError] = useState('');

  // Guard against double-verify (e.g. user double-clicks Accept button)
  const verifyRan = useRef(false);
  const verifyTimeoutRef = useRef(null);

  // Centralised error transition: before showing the error screen, check
  // whether verifyOtp left a partial session in localStorage. The original
  // bug Heidi's prospect hit was exactly this — verifyOtp failed but a
  // session was minted, and the only on-screen action was "Go to Sign In"
  // which left the user in-app with no password set. If we detect a
  // dangling session we hand off to /login?stale_session=1 so the user
  // gets the "set your password or sign out" choice. Otherwise we fall
  // back to the normal error stage with the request-fresh-invite form.
  const transitionToError = async (message) => {
    setFatalError(message);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session?.user) {
        navigate('/login?stale_session=1', { replace: true });
        return;
      }
    } catch (_) {
      // Fall through to the regular error stage on any session-check failure.
    }
    setStage('error');
  };

  // ── Initial mount: extract token from URL, DO NOT call verifyOtp ────────────
  // Calling verifyOtp on mount would let link-protection prefetchers
  // (Microsoft Safe Links, etc.) consume the single-use token before the real
  // user clicks. Instead, capture the token and wait for an explicit click.
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hashString = window.location?.hash?.startsWith('#') ? window.location?.hash?.slice(1) : '';
    const hashParams = new URLSearchParams(hashString);

    const tokenHash = searchParams?.get('token_hash') || hashParams?.get('token_hash');
    const typeParam = searchParams?.get('type') || hashParams?.get('type') || 'invite';
    const accessToken = searchParams?.get('access_token') || hashParams?.get('access_token');
    const refreshToken = searchParams?.get('refresh_token') || hashParams?.get('refresh_token');

    console.log('[invite-setup] Mount — URL params:', {
      tokenHash: tokenHash ? tokenHash?.substring(0, 20) + '...' : 'NONE',
      typeParam,
      accessToken: accessToken ? 'PRESENT' : 'NONE',
    });

    // Always clear URL params immediately — prevents token from lingering in
    // browser history, logs, referrers, or re-triggering on re-mount.
    if (tokenHash || accessToken) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (tokenHash) {
      setPendingToken({ kind: 'token_hash', tokenHash, typeParam });
      setStage('ready');
      return;
    }

    if (accessToken) {
      setPendingToken({ kind: 'access_token', accessToken, refreshToken: refreshToken || '' });
      setStage('ready');
      return;
    }

    // No token in URL — three cases:
    //   (a) genuine post-verification re-mount (rare, e.g. user refreshes mid-flow):
    //       sessionStorage flag set by handleAcceptClick is still present.
    //   (b) already set-up user landed here (clicked stale invite email, browser
    //       autocomplete, etc.) and Heidi's class of bug is to silently re-write
    //       their password via the form's updateUser call. Detect via profile
    //       completion (full_name + is_active) and redirect to their dashboard.
    //   (c) genuinely no session — link consumed/expired with no recovery path.
    (async () => {
      try {
        const justVerified = sessionStorage.getItem('sp_invite_just_verified') === '1';
        sessionStorage.removeItem('sp_invite_just_verified');

        const { data: { session } } = await supabase?.auth?.getSession();
        if (!session?.user) {
          await transitionToError('This invite link has already been used or has expired. Request a fresh invite below.');
          return;
        }

        if (justVerified) {
          console.log('[invite-setup] Post-verification re-mount detected via sessionStorage flag — showing form');
          await loadMetadataAndShowForm(session.user);
          return;
        }

        // Existing session, no flag — likely an already-set-up user landing here
        // by mistake. Check user_profiles to confirm setup completion.
        const { data: existingProfile, error: profileError } = await supabase
          ?.from('user_profiles')
          ?.select('role, full_name, is_active')
          ?.eq('id', session?.user?.id)
          ?.maybeSingle();

        const setupComplete =
          !profileError &&
          !!(existingProfile?.full_name && existingProfile?.is_active === true);

        if (setupComplete) {
          console.log('[invite-setup] Already-set-up user landed on /invite-setup — redirecting to role dashboard');
          const dashboard = ROLE_DASHBOARD?.[existingProfile?.role] || '/worker-home';
          navigate(dashboard, { replace: true });
          return;
        }

        // Profile incomplete — treat as legitimate re-mount (e.g. token verified
        // upstream by /auth-callback before forwarding here).
        await loadMetadataAndShowForm(session.user);
      } catch (_) {
        await transitionToError('This invite link has already been used or has expired. Request a fresh invite below.');
      }
    })();

    return () => {
      if (verifyTimeoutRef?.current) clearTimeout(verifyTimeoutRef?.current);
    };
  }, []);

  // ── User clicked "Accept Invitation" — run the actual verifyOtp ─────────────
  const handleAcceptClick = async () => {
    if (verifyRan?.current) return;
    verifyRan.current = true;

    if (!pendingToken) {
      await transitionToError('This invite link has already been used or has expired. Request a fresh invite below.');
      return;
    }

    setStage('verifying');

    // Mark this as a genuine in-flight verification so a re-mount after token
    // consume (URL params cleared) is treated as legitimate rather than as an
    // "already set up user wandered here" event. Cleared on form submit.
    try {
      sessionStorage.setItem('sp_invite_just_verified', '1');
    } catch (_) {}

    verifyTimeoutRef.current = setTimeout(async () => {
      // Capture the current stage synchronously by reading through setStage,
      // then only transition if we're still verifying. The async session
      // check inside transitionToError is fine because the stage check
      // already happened.
      let wasVerifying = false;
      setStage(prev => {
        if (prev === 'verifying') wasVerifying = true;
        return prev;
      });
      if (wasVerifying) {
        await transitionToError('Verification timed out. The invite link may have expired or a network error occurred.');
      }
    }, 15000);

    try {
      // Sign out any existing session (e.g. admin testing their own invite)
      // before consuming the invite token.
      try {
        await supabase?.auth?.signOut();
      } catch (_) {}

      let resolvedUser = null;

      if (pendingToken?.kind === 'token_hash') {
        const { tokenHash, typeParam } = pendingToken;
        console.log('[invite-setup] Verifying token_hash on click | type:', typeParam);

        const { data: otpData, error: otpError } = await supabase?.auth?.verifyOtp({
          token_hash: tokenHash,
          type: typeParam === 'invite' ? 'invite' : typeParam,
        });

        if (otpError || !otpData?.user) {
          console.warn('[invite-setup] verifyOtp failed with type:', typeParam, '| error:', otpError?.message, '— retrying with type: email');
          const { data: retryData, error: retryError } = await supabase?.auth?.verifyOtp({
            token_hash: tokenHash,
            type: 'email',
          });

          if (retryError || !retryData?.user) {
            console.error('[invite-setup] verifyOtp retry also failed:', retryError?.message);
            await transitionToError('This invite link has expired or has already been used. Request a fresh invite below.');
            return;
          }
          resolvedUser = retryData?.user;
        } else {
          resolvedUser = otpData?.user;
        }
      } else if (pendingToken?.kind === 'access_token') {
        const { accessToken, refreshToken } = pendingToken;
        const { data: sessionData, error: sessionError } = await supabase?.auth?.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (sessionError || !sessionData?.user) {
          await transitionToError('This invite link has expired or has already been used. Request a fresh invite below.');
          return;
        }
        resolvedUser = sessionData?.user;
      }

      console.log('[invite-setup] Token verified successfully, user:', resolvedUser?.id);
      await loadMetadataAndShowForm(resolvedUser);
    } catch (err) {
      console.error('[invite-setup] Unexpected verify error:', err?.message);
      await transitionToError('Something went wrong. Please try again or contact support.');
    }
  };

  // Fresh-invite request — calls the public request-fresh-invite edge
  // function. Always shows the same generic success message regardless of
  // outcome so a stranger sniffing the form can't enumerate which addresses
  // are registered. The only distinguishable state is the 429 rate-limit
  // response, which is acceptable noise.
  const handleRequestFreshInvite = async (e) => {
    e?.preventDefault();
    const trimmedEmail = freshInviteEmail?.trim();
    if (!trimmedEmail) {
      setFreshInviteState('error');
      setFreshInviteError('Please enter your email address.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(trimmedEmail)) {
      setFreshInviteState('error');
      setFreshInviteError('Please enter a valid email address.');
      return;
    }

    setFreshInviteState('sending');
    setFreshInviteError('');

    try {
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/request-fresh-invite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': anonKey,
          },
          body: JSON.stringify({ email: trimmedEmail }),
        }
      );

      if (response?.status === 429) {
        setFreshInviteState('rate_limited');
        return;
      }
      if (!response?.ok) {
        setFreshInviteState('error');
        setFreshInviteError('Something went wrong. Please try again in a moment.');
        return;
      }
      setFreshInviteState('sent');
    } catch (err) {
      console.error('[invite-setup] request-fresh-invite failed:', err?.message);
      setFreshInviteState('error');
      setFreshInviteError('Network error. Please check your connection and try again.');
    }
  };

  // ── Resolve user metadata and transition to the form ────────────────────────
  const loadMetadataAndShowForm = async (resolvedUser) => {
    let freshUser = resolvedUser;
    try {
      const { data: freshData } = await supabase?.auth?.getUser();
      if (freshData?.user?.id) {
        freshUser = freshData?.user;
      }
    } catch (_) {}

    const meta = freshUser?.user_metadata ?? {};
    const email = freshUser?.email ?? '';

    let role = meta?.role ? String(meta.role)?.trim() : null;
    let companyId = meta?.company_id ? String(meta.company_id)?.trim() : null;
    const siteId = meta?.site_id ? String(meta.site_id)?.trim() : null;
    const name = meta?.full_name || meta?.name || '';

    // Fallback: query invite row via RPC if metadata missing
    if (!role || !companyId) {
      try {
        const { data: rpcRows, error: rpcError } = await supabase
          ?.rpc('get_invite_company_by_email', {
            p_email: email,
            p_user_id: freshUser?.id ?? null,
          });

        if (!rpcError && rpcRows && rpcRows?.length > 0) {
          const inviteRow = rpcRows?.[0];
          if (!role && inviteRow?.role) role = inviteRow?.role;
          if (!companyId && inviteRow?.company_id) companyId = inviteRow?.company_id;
        } else {
          try {
            const { data: cuRows } = await supabase
              ?.from('company_users')
              ?.select('company_id, role')
              ?.eq('user_id', freshUser?.id)
              ?.order('created_at', { ascending: false })
              ?.limit(1);

            if (cuRows && cuRows?.length > 0) {
              const cuRow = cuRows?.[0];
              if (!role && cuRow?.role) role = cuRow?.role;
              if (!companyId && cuRow?.company_id) companyId = cuRow?.company_id;
            }
          } catch (_) {}
        }
      } catch (_) {}
    }

    if (!role || (role === 'admin' && !companyId)) {
      role = 'worker';
    }

    setInviteEmail(email);
    setFullName(name);
    setInviteRole(role);
    setInviteCompanyId(companyId);
    setInviteSiteId(siteId);
    setResolvedUserId(freshUser?.id);

    if (verifyTimeoutRef?.current) clearTimeout(verifyTimeoutRef?.current);
    setStage('form');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setFormError('');

    if (!fullName?.trim()) { setFormError('Please enter your full name.'); return; }
    if (!password) { setFormError('Please enter a password.'); return; }
    if (password?.length < 8) { setFormError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setFormError('Passwords do not match.'); return; }

    setStage('submitting');

    // Setup is being completed — clear the in-flight flag so a subsequent
    // re-mount (e.g. browser back to /invite-setup) is treated as an
    // already-set-up event and redirects to the dashboard.
    try {
      sessionStorage.removeItem('sp_invite_just_verified');
    } catch (_) {}

    try {
      const { error: updateError } = await supabase?.auth?.updateUser({
        password,
        data: { full_name: fullName?.trim() },
      });

      if (updateError) {
        setFormError(updateError?.message || 'Failed to set password. Please try again.');
        setStage('form');
        return;
      }

      const { data: sessionData } = await supabase?.auth?.getSession();
      const userId = sessionData?.session?.user?.id || resolvedUserId;

      if (!userId) {
        setFormError('Session expired. Please sign in again.');
        setStage('form');
        return;
      }

      const profilePayload = {
        id: userId,
        full_name: fullName?.trim(),
        role: inviteRole,
        is_active: true,
        ...(inviteCompanyId ? { company_id: inviteCompanyId } : {}),
      };

      await supabase?.from('user_profiles')?.upsert(profilePayload, { onConflict: 'id' });

      // Service-role activation. The client-side upsert above already sets
      // is_active=true, but the immediately-post-updateUser session has been
      // observed to fail RLS silently for that column, leaving accepted users
      // stuck with is_active=false. The edge function flips the flag with
      // service role scoped to the caller's own user id — belt-and-braces.
      try {
        const { data: sessionAfter } = await supabase?.auth?.getSession();
        const accessToken = sessionAfter?.session?.access_token;
        if (accessToken) {
          await supabase?.functions?.invoke('complete-invite-setup', {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
        }
      } catch (activationErr) {
        // Non-fatal: the client-side upsert likely already flipped is_active.
        // If it didn't, an admin can still unblock by editing the profile.
        console.warn('[invite-setup] complete-invite-setup invoke failed:', activationErr?.message);
      }

      if (inviteCompanyId) {
        await supabase?.from('company_users')?.upsert(
          { user_id: userId, company_id: inviteCompanyId, role: inviteRole },
          { onConflict: 'user_id,company_id' }
        );
      }

      if (inviteSiteId && inviteCompanyId && (inviteRole === 'worker' || inviteRole === 'site_manager')) {
        await supabase?.from('worker_site_assignments')?.upsert(
          { user_id: userId, site_id: inviteSiteId, company_id: inviteCompanyId },
          { onConflict: 'user_id,site_id' }
        );
      }

      // Determine redirect
      let profileRoleInDB = null;
      try {
        const { data: profileRow } = await supabase
          ?.from('user_profiles')
          ?.select('role')
          ?.eq('id', userId)
          ?.maybeSingle();
        profileRoleInDB = profileRow?.role || null;
      } catch (_) {}

      const effectiveRole = profileRoleInDB || inviteRole;
      const destination = ROLE_DASHBOARD?.[effectiveRole] || '/worker-home';

      navigate(destination, { replace: true });

    } catch (err) {
      setFormError('Something went wrong. Please try again.');
      setStage('form');
    }
  };

  const isSubmitting = stage === 'submitting';
  const isVerifying = stage === 'verifying';

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <SiteProofLogo className="h-10 w-auto" />
        </div>

        {/* ── Loading state (very brief — URL parsing only) ── */}
        {stage === 'loading' && (
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-400 text-sm">Loading your invitation…</p>
          </div>
        )}

        {/* ── Ready state — awaiting user click to verify token ── */}
        {stage === 'ready' && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 shadow-xl text-center">
            <div className="w-14 h-14 bg-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-5">
              <svg className="w-7 h-7 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">You've been invited to SiteProof</h1>
            <p className="text-gray-400 text-sm mb-8">
              Click below to accept your invitation and set up your account.
            </p>
            <button
              onClick={handleAcceptClick}
              className="w-full py-3 px-6 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors text-sm"
            >
              Accept Invitation
            </button>
          </div>
        )}

        {/* ── Verifying state (post-click) ── */}
        {isVerifying && (
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-400 text-sm">Verifying your invitation…</p>
          </div>
        )}

        {/* ── Error state ── */}
        {stage === 'error' && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 shadow-xl">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-red-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white mb-2">This invite link is no longer valid</h2>
              <p className="text-gray-400 text-sm">
                If you received multiple invitation emails, please open the most recent one. Otherwise, request a fresh invite below.
              </p>
            </div>

            {freshInviteState === 'sent' ? (
              <div className="bg-emerald-900/30 border border-emerald-700/50 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-emerald-200">
                    If your address is on file, we've sent a fresh invite. Check your inbox in the next minute.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleRequestFreshInvite} noValidate className="space-y-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Email address</label>
                  <input
                    type="email"
                    value={freshInviteEmail}
                    onChange={(e) => {
                      setFreshInviteEmail(e?.target?.value);
                      if (freshInviteState === 'error' || freshInviteState === 'rate_limited') {
                        setFreshInviteState('idle');
                        setFreshInviteError('');
                      }
                    }}
                    placeholder="you@example.com"
                    autoComplete="email"
                    disabled={freshInviteState === 'sending'}
                    className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                </div>

                {freshInviteState === 'rate_limited' && (
                  <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg px-4 py-3">
                    <p className="text-sm text-amber-200">Too many requests. Please wait a minute and try again.</p>
                  </div>
                )}

                {freshInviteState === 'error' && freshInviteError && (
                  <div className="bg-red-900/30 border border-red-700/50 rounded-lg px-4 py-3">
                    <p className="text-sm text-red-300">{freshInviteError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={freshInviteState === 'sending'}
                  className="w-full py-3 px-6 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                >
                  {freshInviteState === 'sending' ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending fresh invite…
                    </>
                  ) : (
                    'Send fresh invite'
                  )}
                </button>
              </form>
            )}

            <button
              onClick={() => navigate('/login', { replace: true })}
              className="w-full py-2.5 px-6 text-gray-400 hover:text-white text-sm transition-colors"
            >
              Go to Sign In
            </button>
          </div>
        )}

        {/* ── Form / submitting state ── */}
        {(stage === 'form' || stage === 'submitting') && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 shadow-xl">
            <h1 className="text-2xl font-bold text-white mb-2">Set up your account</h1>
            <p className="text-gray-400 text-sm mb-8">
              You've been invited to SiteProof. Create a password to complete your account setup.
            </p>

            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Email address</label>
                <input
                  type="email"
                  value={inviteEmail}
                  readOnly
                  disabled
                  className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 cursor-not-allowed text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">This is the email your invitation was sent to.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Full name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e?.target?.value)}
                  placeholder="Enter your full name"
                  autoComplete="name"
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Confirm password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e?.target?.value)}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>

              {formError && (
                <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 text-red-300 text-sm">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 px-6 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors text-sm mt-2 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Setting up your account…
                  </>
                ) : (
                  'Complete Setup'
                )}
              </button>
            </form>
          </div>
        )}

        <p className="text-center text-gray-600 text-xs mt-6">
          © {new Date()?.getFullYear()} SiteProof. All rights reserved.
        </p>
      </div>
    </div>
  );
};

// ── Exported wrapper with error boundary ─────────────────────────────────────
const InviteSetup = () => (
  <InviteSetupErrorBoundary>
    <InviteSetupInner />
  </InviteSetupErrorBoundary>
);

export default InviteSetup;
